import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Box } from '@mui/material'
import { blueGrey, green } from '@mui/material/colors'
import { QueryKey } from '@tanstack/react-query'
import { useDeletePOLineItemMatch } from '@/service-library/hooks/po-matches'
import { showErrorSnackbar } from '@/utils/snackbars'
import MatchDetailsPopper from './MatchDetailsPopper'

export type MatchLineData = {
  id: string
  sourceRowId: string
  targetRowId: string
  allReasons: string[]
  aiReasoning: string
  poLineItemMatchId: string
  onDelete: () => void
}

const MATCH_COLOR = blueGrey[400]
const SELECTED_COLOR = green[600]
const DOT_RADIUS = 5
const HIT_THRESHOLD = 20
const PANEL_PADDING = 16 // matches p: 2 on the side panels

type DragOrigin = 'invoice' | 'po'

type DragState = {
  sourceRowId: string
  sourceY: number
  currentX: number
  currentY: number
  origin: DragOrigin
}

type ConnectorCanvasProps = {
  matches: MatchLineData[]
  invoiceRowPositions: Record<string, number>
  poRowPositions: Record<string, number>
  connectedInvoiceRowIds: Set<string>
  connectedPoRowIds: Set<string>
  poLineItemMatchesQueryKey: QueryKey
  onConnect: (invoiceRowId: string, poRowId: string) => void
  onSelectionChange?: (
    selection: {
      invoiceRowId: string
      poRowIds: string[]
    } | null,
  ) => void
}

type DragLineProps = {
  dragState: DragState
  svgWidth: number
  poRowPositions: Record<string, number>
  invoiceRowPositions: Record<string, number>
  hoveredDropTargetId: string | null
}

function DragLine({
  dragState,
  svgWidth,
  poRowPositions,
  invoiceRowPositions,
  hoveredDropTargetId,
}: DragLineProps) {
  const isInvoiceDrag = dragState.origin === 'invoice'
  const sourceX = isInvoiceDrag ? -PANEL_PADDING : svgWidth + PANEL_PADDING
  const targetPositions = isInvoiceDrag ? poRowPositions : invoiceRowPositions
  const snapTarget = hoveredDropTargetId
    ? {
        x: isInvoiceDrag ? svgWidth + PANEL_PADDING : -PANEL_PADDING,
        y: targetPositions[hoveredDropTargetId],
      }
    : null
  const endX = snapTarget ? snapTarget.x : dragState.currentX
  const endY = snapTarget ? snapTarget.y : dragState.currentY
  const cp1x = sourceX + (endX - sourceX) * 0.75
  const cp2x = sourceX + (endX - sourceX) * 0.25
  return (
    <g>
      <path
        d={`M ${sourceX},${dragState.sourceY} C ${cp1x},${dragState.sourceY} ${cp2x},${endY} ${endX},${endY}`}
        fill="none"
        stroke={MATCH_COLOR}
        strokeWidth={2}
        strokeDasharray="6 3"
        strokeOpacity={0.8}
        style={{
          pointerEvents: 'none',
          animation: 'dashdraw 0.4s linear infinite',
        }}
      />
      <circle
        cx={sourceX}
        cy={dragState.sourceY}
        r={DOT_RADIUS}
        fill={MATCH_COLOR}
        style={{ pointerEvents: 'none' }}
      />
    </g>
  )
}

export default function ConnectorCanvas({
  matches,
  invoiceRowPositions,
  poRowPositions,
  connectedInvoiceRowIds,
  connectedPoRowIds,
  poLineItemMatchesQueryKey,
  onConnect,
  onSelectionChange,
}: ConnectorCanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [svgWidth, setSvgWidth] = useState(100)
  const [dragState, setDragState] = useState<DragState | null>(null)
  const [hoveredMatchId, setHoveredMatchId] = useState<string | null>(null)
  const [selectedMatch, setSelectedMatch] = useState<MatchLineData | null>(null)
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  // IDs of matches sharing the same invoice row as the selected match
  const siblingMatchIds = useMemo(() => {
    if (!selectedMatch) return new Set<string>()
    return new Set(
      matches
        .filter(
          (m) =>
            m.sourceRowId === selectedMatch.sourceRowId &&
            m.id !== selectedMatch.id,
        )
        .map((m) => m.id),
    )
  }, [selectedMatch, matches])

  // Compute which dot on the opposite side is closest to cursor during drag
  const hoveredDropTargetId = useMemo(() => {
    if (!dragState) return null
    // Only snap when cursor is in the far half toward the target side
    const isInvoiceDrag = dragState.origin === 'invoice'
    if (isInvoiceDrag && dragState.currentX < svgWidth / 2) return null
    if (!isInvoiceDrag && dragState.currentX > svgWidth / 2) return null
    // Build set of existing match pairs to prevent duplicates
    const existingPairs = new Set(
      matches.map((m) => `${m.sourceRowId}::${m.targetRowId}`),
    )
    const targetPositions = isInvoiceDrag ? poRowPositions : invoiceRowPositions
    let closestId: string | null = null
    let closestDist = HIT_THRESHOLD
    for (const [rowId, y] of Object.entries(targetPositions)) {
      // Check both pair directions for duplicate prevention
      const pairKey = isInvoiceDrag
        ? `${dragState.sourceRowId}::${rowId}`
        : `${rowId}::${dragState.sourceRowId}`
      if (existingPairs.has(pairKey)) continue
      const dist = Math.abs(y - dragState.currentY)
      if (dist < closestDist) {
        closestDist = dist
        closestId = rowId
      }
    }
    return closestId
  }, [dragState, svgWidth, poRowPositions, invoiceRowPositions, matches])

  const { deletePOLineItemMatch, isPending: isDeleting } =
    useDeletePOLineItemMatch({
      listQueryKey: poLineItemMatchesQueryKey,
      onError: () =>
        showErrorSnackbar('Failed to delete match. Please try again.'),
    })

  // Track SVG width via ResizeObserver
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSvgWidth(entry.contentRect.width)
      }
    })
    observer.observe(svg)
    return () => observer.disconnect()
  }, [])

  // Bezier path generator — S-curve shape
  const makePath = useCallback(
    (sourceY: number, targetY: number) => {
      const x0 = -PANEL_PADDING
      const x1 = svgWidth + PANEL_PADDING
      const cp1x = svgWidth * 0.75
      const cp2x = svgWidth * 0.25
      return `M ${x0},${sourceY} C ${cp1x},${sourceY} ${cp2x},${targetY} ${x1},${targetY}`
    },
    [svgWidth],
  )

  // Drag-to-connect: mousedown on a dot
  const handleDotMouseDown = useCallback(
    (rowId: string, y: number, origin: DragOrigin) => (e: React.MouseEvent) => {
      e.preventDefault()
      const svgRect = svgRef.current?.getBoundingClientRect()
      if (!svgRect) return
      setDragState({
        sourceRowId: rowId,
        sourceY: y,
        currentX: e.clientX - svgRect.left,
        currentY: e.clientY - svgRect.top,
        origin,
      })
    },
    [],
  )

  // Document-level listeners for drag
  useEffect(() => {
    if (!dragState) return

    const handleMouseMove = (e: MouseEvent) => {
      const svgRect = svgRef.current?.getBoundingClientRect()
      if (!svgRect) return
      setDragState((prev) =>
        prev
          ? {
              ...prev,
              currentX: e.clientX - svgRect.left,
              currentY: e.clientY - svgRect.top,
            }
          : null,
      )
    }

    const handleMouseUp = (e: MouseEvent) => {
      const svgRect = svgRef.current?.getBoundingClientRect()
      if (!svgRect) return
      const mouseX = e.clientX - svgRect.left
      const mouseY = e.clientY - svgRect.top

      let closestRowId: string | null = null
      const isInvoiceDrag = dragState?.origin === 'invoice'

      // Only allow drop when cursor is in the far half toward the target side
      const inDropZone = isInvoiceDrag
        ? mouseX >= svgWidth / 2
        : mouseX <= svgWidth / 2

      if (inDropZone) {
        // Build set of existing match pairs to prevent duplicates
        const existingPairs = new Set(
          matches.map((m) => `${m.sourceRowId}::${m.targetRowId}`),
        )

        const targetPositions = isInvoiceDrag
          ? poRowPositions
          : invoiceRowPositions
        let closestDist = HIT_THRESHOLD

        for (const [rowId, y] of Object.entries(targetPositions)) {
          if (dragState) {
            const pairKey = isInvoiceDrag
              ? `${dragState.sourceRowId}::${rowId}`
              : `${rowId}::${dragState.sourceRowId}`
            if (existingPairs.has(pairKey)) continue
          }
          const dist = Math.abs(y - mouseY)
          if (dist < closestDist) {
            closestDist = dist
            closestRowId = rowId
          }
        }
      }

      if (closestRowId && dragState) {
        if (isInvoiceDrag) {
          onConnect(dragState.sourceRowId, closestRowId)
        } else {
          onConnect(closestRowId, dragState.sourceRowId)
        }
      }

      setDragState(null)
    }

    document.body.style.cursor = 'crosshair'

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.body.style.cursor = ''
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [
    dragState,
    poRowPositions,
    connectedPoRowIds,
    onConnect,
    svgWidth,
    matches,
    invoiceRowPositions,
  ])

  // When a line is clicked while the popper is open, the ClickAwayListener
  // fires onClose before the new line's onClick can update the popper.
  // This ref lets us skip that close so the popper switches to the new match.
  // If this is true, the popper won't close on click-away.
  const skipNextCloseRef = useRef(false)

  // Click on a match line
  const handleLineClick = useCallback(
    (match: MatchLineData, e: React.MouseEvent) => {
      if (selectedMatch) {
        skipNextCloseRef.current = true
      }
      setSelectedMatch(match)
      setPopoverPosition({ top: e.clientY + 20, left: e.clientX })
      onSelectionChange?.({
        invoiceRowId: match.sourceRowId,
        poRowIds: [match.targetRowId],
      })
    },
    [onSelectionChange, selectedMatch],
  )

  const handleClosePopover = useCallback(() => {
    if (skipNextCloseRef.current) {
      skipNextCloseRef.current = false
      return
    }
    setSelectedMatch(null)
    setPopoverPosition(null)
    onSelectionChange?.(null)
  }, [onSelectionChange])

  // Delete a match
  const handleDelete = useCallback(() => {
    if (!selectedMatch) return
    selectedMatch.onDelete()
    deletePOLineItemMatch(selectedMatch.poLineItemMatchId)
    handleClosePopover()
  }, [selectedMatch, deletePOLineItemMatch, handleClosePopover])

  // Delete key hotkey
  useHotkeys(
    ['delete', 'backspace'],
    () => {
      if (selectedMatch && !isDeleting) {
        handleDelete()
      }
    },
    { enabled: !!selectedMatch },
    [selectedMatch, isDeleting, handleDelete],
  )

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        style={{
          display: 'block',
          overflow: 'visible',
          cursor: dragState ? 'crosshair' : undefined,
        }}
      >
        <style>{`
          @keyframes dashdraw { to { stroke-dashoffset: -10; } }
          @keyframes dropGlow {
            0%, 100% { r: ${DOT_RADIUS}; filter: drop-shadow(0 0 0 transparent); }
            50% { r: ${
              DOT_RADIUS + 3
            }; filter: drop-shadow(0 0 6px ${MATCH_COLOR}); }
          }
        `}</style>
        {/* Match lines */}
        {matches.map((match) => {
          const sourceY = invoiceRowPositions[match.sourceRowId]
          const targetY = poRowPositions[match.targetRowId]
          if (sourceY == null || targetY == null) return null

          const isHovered = hoveredMatchId === match.id && !dragState
          const isSelected = selectedMatch?.id === match.id
          const isSibling = siblingMatchIds.has(match.id)
          const isActive = isHovered || isSelected
          const isHighlighted = isActive || isSibling
          const isDimmed = !!selectedMatch && !isSelected
          const lineColor =
            isSelected || isSibling ? SELECTED_COLOR : MATCH_COLOR

          return (
            <g
              key={match.id}
              style={{
                opacity: isDimmed ? 0.3 : 1,
                transition: 'opacity 180ms ease',
              }}
            >
              {/* Invisible wider path for easier click targeting */}
              <path
                d={makePath(sourceY, targetY)}
                fill="none"
                stroke="transparent"
                strokeWidth={16}
                style={{ cursor: 'pointer' }}
                onClick={(e) => handleLineClick(match, e)}
                onMouseEnter={() => setHoveredMatchId(match.id)}
                onMouseLeave={() => setHoveredMatchId(null)}
              />
              {/* Visible path */}
              <path
                d={makePath(sourceY, targetY)}
                fill="none"
                stroke={lineColor}
                strokeWidth={isActive ? 3 : 2}
                strokeOpacity={isHighlighted ? 1 : 0.6}
                strokeDasharray={isActive ? '6 4' : undefined}
                style={{
                  filter: isActive
                    ? 'drop-shadow(0 0 4px rgba(46, 125, 50, 0.9))'
                    : undefined,
                  animation: isActive
                    ? 'dashdraw 0.4s linear infinite'
                    : undefined,
                  transition:
                    'stroke-width 180ms ease, stroke-opacity 180ms ease, filter 180ms ease',
                  pointerEvents: 'none',
                }}
              />
              {/* Endpoint dots */}
              <circle
                cx={-PANEL_PADDING}
                cy={sourceY}
                r={DOT_RADIUS}
                fill={lineColor}
                style={{ pointerEvents: 'none' }}
              />
              <circle
                cx={svgWidth + PANEL_PADDING}
                cy={targetY}
                r={DOT_RADIUS}
                fill={lineColor}
                style={{ pointerEvents: 'none' }}
              />
            </g>
          )
        })}

        {/* Drag handle dots for invoice rows — half over card edge */}
        {Object.entries(invoiceRowPositions).map(([rowId, y]) => {
          const isConnected = connectedInvoiceRowIds.has(rowId)
          const isDropTarget =
            dragState?.origin === 'po' && hoveredDropTargetId === rowId
          return (
            <circle
              key={`inv-dot-${rowId}`}
              cx={-PANEL_PADDING}
              cy={y}
              r={DOT_RADIUS}
              fill={isDropTarget || isConnected ? MATCH_COLOR : '#bdbdbd'}
              stroke={isDropTarget || isConnected ? MATCH_COLOR : '#9e9e9e'}
              strokeWidth={1.5}
              style={{
                cursor: 'crosshair',
                transition: 'fill 200ms ease, stroke 200ms ease',
                animation: isDropTarget
                  ? 'dropGlow 1s ease-in-out infinite'
                  : undefined,
              }}
              onMouseDown={handleDotMouseDown(rowId, y, 'invoice')}
            />
          )
        })}

        {/* Drag/drop dots for PO rows — half over card edge */}
        {Object.entries(poRowPositions).map(([rowId, y]) => {
          const isConnected = connectedPoRowIds.has(rowId)
          const isDropTarget =
            dragState?.origin === 'invoice' && hoveredDropTargetId === rowId
          return (
            <circle
              key={`po-dot-${rowId}`}
              cx={svgWidth + PANEL_PADDING}
              cy={y}
              r={DOT_RADIUS}
              fill={isDropTarget || isConnected ? MATCH_COLOR : '#bdbdbd'}
              stroke={isDropTarget || isConnected ? MATCH_COLOR : '#9e9e9e'}
              strokeWidth={1.5}
              style={{
                cursor: 'crosshair',
                transition: 'fill 200ms ease, stroke 200ms ease',
                animation: isDropTarget
                  ? 'dropGlow 1s ease-in-out infinite'
                  : undefined,
              }}
              onMouseDown={handleDotMouseDown(rowId, y, 'po')}
            />
          )
        })}

        {/* Temporary drag line */}
        {dragState && (
          <DragLine
            dragState={dragState}
            svgWidth={svgWidth}
            poRowPositions={poRowPositions}
            invoiceRowPositions={invoiceRowPositions}
            hoveredDropTargetId={hoveredDropTargetId}
          />
        )}
      </svg>

      <MatchDetailsPopper
        selectedMatch={selectedMatch}
        popoverPosition={popoverPosition}
        isDeleting={isDeleting}
        onClose={handleClosePopover}
        onDelete={handleDelete}
      />
    </Box>
  )
}
